import { useMemo } from 'react'

import { BrokenImageOutlined, VideocamOffOutlined } from '@mui/icons-material'
import { Tooltip, Typography } from '@mui/material'
import { VideoStream } from '@nativewaves/nw-cloud-sdk/content'
import { ColumnDef } from '@tanstack/react-table'
import { upperFirst } from 'lodash-es'
import { useTranslation } from 'react-i18next'

import { CopyId } from '@shared/components/Table/Fields'
import { NoSrcPlaceholderWrapper, Thumbnail } from 'domains/Workspaces/Events'
import { formatContentLifecycleDuration } from 'domains/Workspaces/Streams'
import { StateVisualizer } from 'domains/Workspaces/Video/Streams'

export const useColumns = () => {
  const { t } = useTranslation(['common', 'domain'])

  return useMemo<ColumnDef<VideoStream>[]>(
    () => [
      {
        accessorFn: (row) => ({ src: row.thumbnail?.url, alt: row.name || row.id }),
        id: '_thumbnail',
        header: () => null,
        meta: {
          sizing: {
            width: 96,
            px: 2,
          },
          align: 'center',
        },
        cell: ({ getValue }) => {
          const { src, alt } = getValue<{ src?: string; alt: string }>()
          if (src) {
            return (
              <picture>
                <Thumbnail type="image/jpeg" data={src} aria-label={alt}>
                  <NoSrcPlaceholderWrapper>
                    <BrokenImageOutlined />
                  </NoSrcPlaceholderWrapper>
                </Thumbnail>
              </picture>
            )
          }
          return (
            <NoSrcPlaceholderWrapper>
              <VideocamOffOutlined />
            </NoSrcPlaceholderWrapper>
          )
        },
      },
      {
        accessorFn: (row) => (row.name.length ? row.name : t('noNameProvided')),
        header: t('name'),
        meta: {
          sizing: {
            minWidth: 300,
          },
        },
        cell: ({ getValue }) => (
          <Typography variant="inherit" noWrap>
            {getValue<string>()}
          </Typography>
        ),
      },
      {
        accessorFn: (row) => (row.live ? 'live' : 'ended'),
        id: '_status',
        header: () => t('status'),
        meta: {
          sizing: {
            width: 100,
            px: 2,
          },
          align: 'center',
        },
        cell: ({ getValue }) => {
          const state = getValue<'live' | 'ended'>()

          return (
            <StateVisualizer state={state === 'live'} variant="text-sm">
              {upperFirst(state.toLowerCase())}
            </StateVisualizer>
          )
        },
      },
      {
        accessorKey: 'contentLifecyclePolicy',
        header: t('domain:Workspace.Video.contentLifecycle'),
        meta: {
          sizing: {
            width: 180,
            px: 2,
          },
        },
        cell: ({ getValue }) => {
          const duration = formatContentLifecycleDuration(getValue<VideoStream['contentLifecyclePolicy']>().deleteAfter)

          const label = duration ? duration.label : t('domain:Workspace.Video.contentLifecycleNoExpiry')

          const tooltipTitle = duration
            ? t('domain:Workspace.Video.contentLifecycleDeletion', { time: duration.full })
            : t('domain:Workspace.Video.contentLifecycleNoDeletion')

          return (
            <Tooltip title={tooltipTitle} placement="top">
              <Typography>{label}</Typography>
            </Tooltip>
          )
        },
      },
      {
        accessorKey: 'id',
        header: t('id'),
        meta: {
          sizing: {
            width: 200,
            px: 2,
          },
        },
        cell: ({ getValue }) => <CopyId id={getValue<string>()} />,
      },
    ],
    [t],
  )
}
